import React from 'react';
import { Routes, Route } from 'react-router-dom';
import Landing from './components/Landing';
import RecipeGenerator from './components/RecipeGenerator';

// UTM Tracking Helper Functions - DEFINE THIS FIRST
const generateUTMLink = (baseUrl, source, medium, campaign, content = '') => {
  const utmParams = new URLSearchParams({
    utm_source: source,
    utm_medium: medium,
    utm_campaign: campaign,
    utm_content: content
  });
  const separator = baseUrl.includes('?') ? '&' : '?';
  return `${baseUrl}${separator}${utmParams.toString()}`;
};

const trackAffiliateClick = (platform, ingredient, recipeContext = '') => {
  if (window.gtag) {
    window.gtag('event', 'affiliate_click', {
      'platform': platform,
      'ingredient': ingredient,
      'recipe_context': recipeContext,
      'click_source': 'recipe_modal',
      'timestamp': Date.now()
    });
  }
};

const trackLandingCTA = (buttonLocation, buttonText) => {
  if (window.gtag) {
    window.gtag('event', 'landing_cta_click', {
      'button_location': buttonLocation,
      'button_text': buttonText,
      'page': 'landing',
      'timestamp': Date.now()
    });
  }
};


// Amazon Affiliate Link Generator
const generateAmazonLink = (ingredient, category = 'grocery') => {
  const amazonTag = "fridgetofor06-20";
  const searchQuery = encodeURIComponent(ingredient);
  const baseUrl = `https://amazon.com/s?k=${searchQuery}&tag=${amazonTag}&ref=nb_sb_noss`;
  
  return generateUTMLink(baseUrl, 'fridgetofork', 'affiliate', 'recipe_ingredients', ingredient);
};

const generateAmazonProductLink = (productId, productName) => {
  const amazonTag = "fridgetofor06-20"; // Amazon Associates tag
  const baseUrl = `https://amazon.com/dp/${productId}?tag=${amazonTag}`;
  
  return generateUTMLink(baseUrl, 'fridgetofork', 'affiliate', 'kitchen_equipment', productName);
};

// Instacart Link Generator - NOW IT CAN USE generateUTMLink
const generateInstacartLink = (ingredients) => {
  const instacartAffiliateId = "YOUR_INSTACART_AFFILIATE_CODE"; // add your Instacart affiliate program code here
  const searchQuery = encodeURIComponent(Array.isArray(ingredients) ? ingredients.join(' ') : ingredients);
  
  const baseUrl = `https://inst.cr/t/${instacartAffiliateId}?url=${encodeURIComponent(`https://www.instacart.com/store/search?k=${searchQuery}`)}`;
  
  return generateUTMLink(baseUrl, 'fridgetofork', 'affiliate', 'grocery_delivery', 'instacart');
};

const generateAmazonFreshLink = () => {
  const baseUrl = "https://www.amazon.com/alm/storefront?almBrandId=VUZHIFdob2xlIEZvb2Rz";
  
  return generateUTMLink(baseUrl, 'fridgetofork', 'affiliate', 'grocery_delivery', 'amazon_fresh');
};

const generateWalmartLink = (ingredient = '') => {
  const walmartPublisherId = "YOUR_IMPACT_ID"; // Get this from Impact.com
  const campaignId = "YOUR_CAMPAIGN_ID"; // Get this from Impact.com CHANGE WHEN YOU get it set up
  const searchQuery = ingredient ? encodeURIComponent(ingredient) : '';
  
  // Walmart Impact tracking URL format
  const baseUrl = `https://goto.walmart.com/c/${walmartPublisherId}/${campaignId}/0?u=${encodeURIComponent(`https://grocery.walmart.com/search?query=${searchQuery}`)}`;
  
  return generateUTMLink(baseUrl, 'fridgetofork', 'affiliate', 'grocery_delivery', 'walmart');
};

// Kitchen Equipment Recommendations Database
const getKitchenEquipment = (recipeName) => {
  const equipmentDB = {
    "Chicken Stir Fry": [
      { name: "Carbon Steel Wok", productId: "B08X4FJ6QZ", price: "$40-60", category: "cookware" },
      { name: "Bamboo Spatula Set", productId: "B07FJ6XQZ9", price: "$15-25", category: "utensils" },
      { name: "Rice Cooker", productId: "B08QJ6XQFZ", price: "$30-80", category: "appliances" }
    ],
    "Pancakes": [
      { name: "Non-Stick Griddle Pan", productId: "B08XQZF6YZ", price: "$30-50", category: "cookware" },
      { name: "Pancake Batter Dispenser", productId: "B07GQX6QYZ", price: "$12-18", category: "tools" },
      { name: "Silicone Spatula Set", productId: "B08FQX6QYZ", price: "$15-25", category: "utensils" }
    ],
    "Pasta Aglio e Olio": [
      { name: "Large Pasta Pot", productId: "B08QX6FQYZ", price: "$40-70", category: "cookware" },
      { name: "Pasta Server Spoon", productId: "B07QX6FQYZ", price: "$10-20", category: "utensils" },
      { name: "Garlic Press", productId: "B08GX6FQYZ", price: "$15-25", category: "tools" }
    ],
    "Caesar Salad": [
      { name: "Large Salad Bowl", productId: "B08FX6QFYZ", price: "$25-40", category: "serving" },
      { name: "Salad Tongs", productId: "B07FX6QFYZ", price: "$12-20", category: "utensils" },
      { name: "Cheese Grater", productId: "B08GX6QFYZ", price: "$15-30", category: "tools" }
    ]
  };
  
  return equipmentDB[recipeName] || [];
};

// Pantry Staples that work well with Amazon affiliate
const getPantryStaples = (ingredients) => {
  const pantryItems = ['salt', 'pepper', 'olive oil', 'flour', 'sugar', 'baking powder', 'vanilla', 'butter', 'water', 'eggs'];
  return ingredients.filter(ing => pantryItems.includes(ing.searchTerm?.toLowerCase()));
};

function App() {
  // Global affiliate tracking functions available to all components
  const affiliateUtils = {
    generateAmazonLink,
    generateAmazonProductLink,
    generateInstacartLink,
    generateAmazonFreshLink,
    generateWalmartLink,
    trackAffiliateClick,
    trackLandingCTA,
    getKitchenEquipment,
    getPantryStaples,
  };

  return (
    <div className="App">
      <Routes>
        <Route 
          path="/" 
          element={<Landing affiliateUtils={affiliateUtils} />} 
        />
        <Route 
          path="/app" 
          element={<RecipeGenerator affiliateUtils={affiliateUtils} />} 
        />
        <Route 
          path="/recipe-generator" 
          element={<RecipeGenerator affiliateUtils={affiliateUtils} />} 
        />
        <Route 
          path="/landing" 
          element={<Landing affiliateUtils={affiliateUtils} />} 
        />
        {/* Catch-all route - redirect to landing */}
        <Route 
          path="*" 
          element={<Landing affiliateUtils={affiliateUtils} />} 
        />
      </Routes>
    </div>
  );
}

export default App;